import React, { useState, useEffect } from 'react';
import { User } from '../../types/user';
import { Organization, OrganizationUser } from '../../types/organization';
import {
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  doc,
  arrayUnion,
  arrayRemove,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../../lib/firebase';
import {
  UserIcon,
  PlusIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { Tooltip } from '../common/Tooltip';
import Modal from '../common/Modal';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';
interface OrganizationUsersProps {
  organization: Organization;
  onOrganizationUpdate: (org: Organization) => void;
}

const OrganizationUsers: React.FC<OrganizationUsersProps> = ({
  organization,
  onOrganizationUpdate
}) => {
  const [loading, setLoading] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [userToDelete, setUserToDelete] = useState<OrganizationUser | null>(null);
  const { t } = useLanguage();


  // Biztosítjuk, hogy mindig legyen egy üres tömb, ha nincs users
  const users = organization?.users || [];

  // Új felhasználó hozzáadása
  const handleAddUser = async () => {
    try {
      setLoading(true);
      // Felhasználó keresése email alapján
      const q = query(
        collection(db, 'users'),
        where('email', '==', newUserEmail)
      );
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        toast.error(t('common.messages.organization.emailNotFound'));
        return;
      }

      const userDoc = snapshot.docs[0];
      const userData = userDoc.data();

      // Ellenőrizzük, hogy nincs-e már a szervezetben
      if (organization.users.some(u => u.userId === userDoc.id)) {
        toast.error(t('common.messages.organization.userAlreadyExists'));
        return;
      }

      const newOrgUser: OrganizationUser = {
        userId: userDoc.id,
        email: userData.email,
        role: 'user',
        addedAt: new Date()
      };

      // Frissítjük a szervezet dokumentumát
      const orgRef = doc(db, 'organizations', organization.id);
      await updateDoc(orgRef, {
        users: arrayUnion(newOrgUser),
        updatedAt: serverTimestamp()
      });

      // Frissítjük a felhasználó dokumentumát az organizationId-val
      const userRef = doc(db, 'users', userDoc.id);
      await updateDoc(userRef, {
        organizationId: organization.id,
        updatedAt: serverTimestamp()
      });

      // Frissítjük a lokális state-et
      const updatedOrg = {
        ...organization,
        users: [...organization.users, newOrgUser]
      };
      onOrganizationUpdate(updatedOrg);
      setNewUserEmail('');
      toast.success(t('common.messages.organization.userAdded'));
    } catch (error) {
      console.error('Error adding user:', error);
      toast.error(t('common.messages.organization.userAddError'));
    } finally {
      setLoading(false);
    }
  };

  // Felhasználó eltávolítása
  const handleRemoveUser = (user: OrganizationUser) => {
    setUserToDelete(user);
  };

  const confirmRemoveUser = async () => {
    if (!userToDelete) return;

    try {
      setLoading(true);
      // Frissítjük a szervezet dokumentumát
      const orgRef = doc(db, 'organizations', organization.id);
      await updateDoc(orgRef, {
        users: arrayRemove(userToDelete),
        updatedAt: serverTimestamp()
      });

      // Frissítjük a felhasználó dokumentumát, töröljük az organizationId-t
      const userRef = doc(db, 'users', userToDelete.userId);
      await updateDoc(userRef, {
        organizationId: '',
        updatedAt: serverTimestamp()
      });

      // Frissítjük a lokális state-et
      const updatedOrg = {
        ...organization,
        users: organization.users.filter(u => u.userId !== userToDelete.userId)
      };
      onOrganizationUpdate(updatedOrg);
      toast.success(t('common.messages.organization.userRemoved'));
    } catch (error) {
      console.error('Error removing user:', error);
      toast.error(t('common.messages.organization.userRemoveError'));
    } finally {
      setUserToDelete(null);
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 mb-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 sm:gap-2">
        <h3 className="text-lg font-medium text-gray-800">{t('organizations.fields.users.title')}</h3>
        <div className="flex flex-1 sm:flex-none gap-2">
          <input
            type="email"
            value={newUserEmail}
            onChange={(e) => setNewUserEmail(e.target.value)}
            placeholder={t('common.placeholders.email')}
            disabled={loading}
            className="w-full sm:w-auto min-w-0 px-2 sm:px-3 py-1.5 bg-white border border-gray-300 rounded-md text-gray-700 text-sm truncate shadow-sm"
          />
          <button
            onClick={handleAddUser}
            disabled={loading}
            className="flex-shrink-0 flex items-center gap-1 px-2 sm:px-3 py-1.5 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 text-sm whitespace-nowrap"
          >
            <PlusIcon className="w-4 h-4" />
            <span>{t('organizations.fields.users.add')}</span>
          </button>
        </div>
      </div>

      <div className="grid gap-4">
        {users.map(user => (
          <div
            key={user.userId}
            className="flex items-center justify-between p-3 sm:p-4 bg-white rounded-lg border border-gray-200 shadow-sm"
          >
            <div className="flex items-center gap-2 sm:gap-3 min-w-0 pr-2">
              <UserIcon className="w-5 h-5 text-gray-500 flex-shrink-0" />
              <div className="min-w-0 overflow-hidden">
                <div className="font-medium text-gray-800 truncate">{user.email}</div>
                <div className="text-sm text-gray-500">
                  {user.role === 'admin' ? t('organizations.roles.administrator') : t('organizations.roles.user')}
                </div>
              </div>
            </div>
            <Tooltip content="Eltávolítás">
              <button
                onClick={() => handleRemoveUser(user)}
                disabled={loading}
                className="text-gray-500 hover:text-red-500 disabled:opacity-50 flex-shrink-0"
              >
                <TrashIcon className="w-5 h-5" />
              </button>
            </Tooltip>
          </div>
        ))}
      </div>

      <Modal
        isOpen={!!userToDelete}
        onClose={() => setUserToDelete(null)}
        title={t('organizations.fields.users.confirmRemoveTitle')}
      >
        <div className="p-6">
          <p className="text-gray-700 mb-4">
            {t('organizations.fields.users.confirmRemove')}
            <br />
            <span className="text-sm text-gray-500">
              {userToDelete?.email}
            </span>
          </p>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setUserToDelete(null)}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 disabled:opacity-50"
            >
              {t('organizations.fields.users.cancelRemove')}
            </button>
            <button
              onClick={confirmRemoveUser}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-50"
            >
              {t('organizations.fields.users.remove')}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default OrganizationUsers; 